import { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import SubPageHeader from '../components/SubPageHeader';
import { authFetch } from '../config';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const API_BASE = process.env.REACT_APP_INGEST_URL || '';

interface AbgleichStatistik {
  brutto: number;
  defekt: number;
  brutto2: number;
  dublette: number;
  aktiv_rc: number;
  aktiv_mc: number;
  inaktiv_rc: number;
  inaktiv_mc: number;
  blacklist_rc: number;
  blacklist_mc: number;
  bbb: number;
  xtel: number;
  netto: number;
  quote_prozent: number;
}

interface AbgleichRecord {
  id: number;
  vnm: string;
  nnm: string;
  str: string;
  plz: string;
  ort: string;
  hkn: string;
  lfr: string;
  akt: string;
  chk: string;
  inf5: string;
}

interface AbgleichHistoryItem {
  source_id: number;
  name: string;
  file_name: string;
  status: string;
  total_rows: number;
  matched_rows: number;
  unmatched_rows: number;
  created_at: string;
  brutto1: number;
  netto: number;
  quote: number;
  hkn: string;
  liste: string;
  lfr: string;
}

interface AbgleichProgressStep {
  step: string;
  description: string;
  count: number;
  duration_ms: number;
  status: 'pending' | 'running' | 'done';
}

interface AbgleichProgress {
  source_id: number;
  status: 'running' | 'done' | 'error';
  current_step: string;
  steps: AbgleichProgressStep[];
  started_at: number;
  error?: string;
}

export default function Abgleich() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [viewMode, setViewMode] = useState<'new' | 'history'>('new');
  const [step, setStep] = useState<'upload' | 'mapping' | 'abgleich' | 'review'>('upload');
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [columns, setColumns] = useState<any[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [statistik, setStatistik] = useState<AbgleichStatistik | null>(null);
  const [records, setRecords] = useState<AbgleichRecord[]>([]);
  const [recordFilter, setRecordFilter] = useState<'all' | 'netto' | 'gesperrt'>('all');
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [history, setHistory] = useState<AbgleichHistoryItem[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [historyDetail, setHistoryDetail] = useState<any>(null);
  const [progress, setProgress] = useState<AbgleichProgress | null>(null);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [actionRecords, setActionRecords] = useState<AbgleichRecord[]>([]);
  const [actionRecordsLoading, setActionRecordsLoading] = useState(false);
  const [historyAbgleichRunning, setHistoryAbgleichRunning] = useState(false);
  const [historyProgress, setHistoryProgress] = useState<AbgleichProgress | null>(null);
  const [historyTransferLoading, setHistoryTransferLoading] = useState(false);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  // Poll for progress when abgleich is running
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    let retryCount = 0;
    let isCompleted = false; // Flag to prevent double-loading
    const maxRetries = 3;

    if (step === 'abgleich' && sourceId) {
      const pollProgress = async () => {
        // Skip if already completed
        if (isCompleted) return;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

        try {
          const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/progress`, {
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          const data = await response.json();
          retryCount = 0; // Reset on success

          if (data.success && data.data) {
            setProgress(data.data);

            // Check if done or error - then load results
            if (data.data.status === 'done' && !isCompleted) {
              // Mark as completed FIRST to prevent double-execution
              isCompleted = true;
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              // Abgleich fertig - Ergebnisse laden
              await loadResults();
            } else if (data.data.status === 'error') {
              isCompleted = true;
              if (pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
              alert('Abgleich fehlgeschlagen: ' + (data.data.error || 'Unbekannter Fehler'));
              setStep('mapping');
            }
          }
        } catch (err: any) {
          clearTimeout(timeoutId);
          // Ignore AbortError from timeout
          if (err.name === 'AbortError') {
            console.warn('Progress poll timed out, retrying...');
            return;
          }
          retryCount++;
          console.warn(`Progress poll failed (attempt ${retryCount}/${maxRetries}):`, err.message);

          // Don't stop polling on timeout - just log and continue
          if (retryCount >= maxRetries) {
            console.error('Max retries reached, but continuing to poll...');
            retryCount = 0; // Reset and keep trying
          }
        }
      };

      // Load results helper - use /statistik endpoint for correct data format
      const loadResults = async () => {
        console.log('loadResults called for sourceId:', sourceId);
        try {
          const url = `${API_BASE}/api/ingest/abgleich/${sourceId}/statistik`;
          console.log('Fetching statistik from:', url);
          const response = await authFetch(url);
          const data = await response.json();
          console.log('Statistik response:', data);
          if (data.success && data.data) {
            // Map from DB field names to frontend expected names
            const stats = data.data;
            setStatistik({
              brutto: stats.brutto1 || 0,
              defekt: stats.defekt || 0,
              brutto2: stats.brutto2 || 0,
              dublette: stats.dublette || 0,
              aktiv_rc: stats.aktiv_rc || 0,
              aktiv_mc: stats.aktiv_mc || 0,
              inaktiv_rc: stats.inaktiv_rc || 0,
              inaktiv_mc: stats.inaktiv_mc || 0,
              blacklist_rc: stats.blacklist_rc || 0,
              blacklist_mc: stats.blacklist_mc || 0,
              bbb: stats.bbb || 0,
              xtel: stats.xtel || 0,
              netto: stats.netto || 0,
              quote_prozent: stats.quote ? parseFloat(stats.quote) * 100 : 0
            });
            await loadRecords('all');
          } else {
            console.error('Failed to load statistik - invalid response');
          }
        } catch (err) {
          console.error('Failed to load results:', err);
        } finally {
          // ALWAYS go to review step when done, even if loading failed
          setLoading(false);
          setStep('review');
        }
      };

      // Initial poll
      pollProgress();

      // Poll every 1000ms (slower to reduce server load)
      pollInterval = setInterval(pollProgress, 1000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [step, sourceId]);

  // Poll for progress when running Abgleich from history
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    let retryCount = 0;

    if (historyAbgleichRunning && selectedHistoryId) {
      const pollProgress = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
          const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${selectedHistoryId}/progress`, {
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          const data = await response.json();
          retryCount = 0;

          if (data.success && data.data) {
            setHistoryProgress(data.data);

            // Check if done or error
            if (data.data.status === 'done') {
              if (pollInterval) clearInterval(pollInterval);
              setHistoryAbgleichRunning(false);
              setHistoryProgress(null);
              await loadHistory();
              await loadHistoryDetail(selectedHistoryId);
              alert('Abgleich erfolgreich abgeschlossen!');
            } else if (data.data.status === 'error') {
              if (pollInterval) clearInterval(pollInterval);
              setHistoryAbgleichRunning(false);
              setHistoryProgress(null);
              alert('Abgleich fehlgeschlagen: ' + (data.data.error || 'Unbekannter Fehler'));
            }
          }
        } catch (err: any) {
          clearTimeout(timeoutId);
          retryCount++;
          console.warn(`History progress poll failed (attempt ${retryCount}):`, err.message);

          if (retryCount >= 5) {
            retryCount = 0; // Reset and keep trying
          }
        }
      };

      pollProgress();
      pollInterval = setInterval(pollProgress, 1000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [historyAbgleichRunning, selectedHistoryId]);

  const loadHistory = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/history`);
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const loadHistoryDetail = async (id: number) => {
    setLoading(true);
    setSelectedAction(null);
    setActionRecords([]);
    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${id}/protocol`);
      const data = await response.json();
      if (data.success) {
        setHistoryDetail(data.data);
        setSelectedHistoryId(id);
      }
    } catch (err) {
      console.error('Failed to load history detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecordsByAction = async (sourceId: number, action: string) => {
    setActionRecordsLoading(true);
    setSelectedAction(action);
    try {
      const response = await authFetch(
        `${API_BASE}/api/ingest/abgleich/${sourceId}/records-by-action?action=${encodeURIComponent(action)}&limit=100`
      );
      const data = await response.json();
      if (data.success) {
        setActionRecords(data.records);
      }
    } catch (err) {
      console.error('Failed to load records by action:', err);
    } finally {
      setActionRecordsLoading(false);
    }
  };

  const downloadRecordsByAction = async (sourceId: number, action: string) => {
    try {
      const response = await authFetch(
        `${API_BASE}/api/ingest/abgleich/${sourceId}/records-by-action?action=${encodeURIComponent(action)}&format=csv`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `abgleich_${sourceId}_${action}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download records:', err);
    }
  };

  const runAbgleichFromHistory = async (sourceId: number) => {
    setHistoryAbgleichRunning(true);
    setHistoryProgress(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/run`, {
        method: 'POST',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        // Abgleich gestartet - Polling übernimmt
        console.log('History-Abgleich gestartet:', data.status);
      } else {
        alert('Abgleich starten fehlgeschlagen: ' + data.message);
        setHistoryAbgleichRunning(false);
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error('History-Abgleich start error:', err);
      if (err.name === 'AbortError') {
        // Timeout - trotzdem weitermachen
        console.warn('Start-Request Timeout - prüfe Progress via Polling...');
      } else {
        // Show detailed error info for debugging
        const errorDetails = `${err.name || 'Error'}: ${err.message}`;
        console.error('Full error:', errorDetails, err.stack);
        alert('Fehler beim Starten des Abgleichs: ' + errorDetails);
        setHistoryAbgleichRunning(false);
      }
    }
    // Note: setHistoryAbgleichRunning(false) wird vom Polling gemacht
  };

  const transferFromHistory = async (sourceId: number, netto: number) => {
    if (!window.confirm(`${netto.toLocaleString()} Datensätze in den WPool übertragen?`)) return;

    setHistoryTransferLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/transfer`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        alert(`${data.transferred?.toLocaleString() || netto.toLocaleString()} Datensätze erfolgreich in WPool übertragen!`);
        // Reload to update status
        await loadHistory();
        await loadHistoryDetail(sourceId);
      } else {
        alert('Transfer fehlgeschlagen: ' + data.message);
      }
    } catch (err) {
      alert('Fehler beim Transfer: ' + (err as Error).message);
    } finally {
      setHistoryTransferLoading(false);
    }
  };

  const deleteHistoryItem = async (id: number) => {
    if (!window.confirm('Diesen Abgleich wirklich löschen?')) return;

    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        loadHistory();
        if (selectedHistoryId === id) {
          setSelectedHistoryId(null);
          setHistoryDetail(null);
        }
      }
    } catch (err) {
      alert('Löschen fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const abgleichFields = [
    { value: '', label: 'Nicht zuordnen' },
    { value: 'akn', label: 'Anrede (akn)' },
    { value: 'vnm', label: 'Vorname (vnm)' },
    { value: 'nnm', label: 'Nachname (nnm)' },
    { value: 'str', label: 'Straße (str)' },
    { value: 'plz', label: 'PLZ' },
    { value: 'ort', label: 'Ort' },
    { value: 'hkn', label: 'Herkunft (hkn)' },
    { value: 'lfr', label: 'Lieferant (lfr)' },
    { value: 'gtt', label: 'Geburtstag (gtt)' },
    { value: 'gmm', label: 'Geburtsmonat (gmm)' },
    { value: 'gjj', label: 'Geburtsjahr (gjj)' },
    { value: 'tns', label: 'Telefon (tns)' },
    { value: 'ema', label: 'E-Mail (ema)' },
    { value: 'inf1', label: 'Info 1' },
    { value: 'inf2', label: 'Info 2' },
    { value: 'inf5', label: 'Info 5' },
    { value: 'inf6', label: 'Info 6' },
    { value: 'dat5', label: 'Datum (dat5)' },
  ];

  const handleMappingChange = (columnName: string, targetField: string) => {
    const newMapping = { ...mapping };
    if (targetField) {
      newMapping[columnName] = targetField;
    } else {
      delete newMapping[columnName];
    }
    setMapping(newMapping);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setFileName(file.name);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_name', file.name);

    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSourceId(data.data.source_id);
        setColumns(data.data.columns);

        // Auto-mapping basierend auf Vorschlägen
        const initialMapping: Record<string, string> = {};
        data.data.columns.forEach((col: any) => {
          if (col.suggested_mapping) {
            initialMapping[col.name] = col.suggested_mapping;
          }
        });
        setMapping(initialMapping);
        setStep('mapping');
      } else {
        alert('Upload fehlgeschlagen: ' + data.error);
      }
    } catch (err) {
      alert('Fehler beim Upload: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!sourceId) return;

    setLoading(true);
    try {
      await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/mapping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping })
      });

      setStep('abgleich');
      await runAbgleich();
    } catch (err) {
      alert('Fehler beim Mapping: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const runAbgleich = async () => {
    if (!sourceId) return;

    setLoading(true);
    setProgress(null); // Reset progress for new run

    // Start Abgleich asynchronously - don't wait for completion
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout for start

    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/run`, {
        method: 'POST',
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      const data = await response.json();

      if (data.success) {
        // Abgleich wurde gestartet - Polling übernimmt den Rest
        console.log('Abgleich gestartet:', data.status);
        // Der useEffect für Polling übernimmt jetzt
      } else {
        alert('Abgleich starten fehlgeschlagen: ' + data.message);
        setLoading(false);
        setStep('mapping');
      }
    } catch (err: any) {
      clearTimeout(timeoutId);
      console.error('Abgleich start error:', err);
      if (err.name === 'AbortError') {
        // Timeout beim Starten - trotzdem weitermachen, Polling prüft den Status
        console.warn('Start-Request Timeout - prüfe Progress via Polling...');
      } else {
        // Show detailed error info for debugging
        const errorDetails = `${err.name || 'Error'}: ${err.message}`;
        console.error('Full error:', errorDetails, err.stack);
        alert('Fehler beim Starten des Abgleichs: ' + errorDetails);
        setLoading(false);
        setStep('mapping');
      }
    }
    // Note: setLoading(false) wird vom Polling gemacht wenn fertig
  };

  const loadRecords = async (filter: 'all' | 'netto' | 'gesperrt') => {
    if (!sourceId) return;

    const response = await authFetch(
      `${API_BASE}/api/ingest/abgleich/${sourceId}/records?status=${filter}&limit=100`
    );
    const data = await response.json();
    setRecords(data.records || []);
    setRecordFilter(filter);
  };

  const handleTransfer = async () => {
    if (!sourceId || !statistik) return;

    if (!window.confirm(`${statistik.netto} Datensätze in den WPool übertragen?`)) return;

    setLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/transfer`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        alert(`Erfolgreich: ${data.transferred_count} Datensätze übertragen`);
        // Reset
        setStep('upload');
        setSourceId(null);
        setColumns([]);
        setMapping({});
        setStatistik(null);
        setRecords([]);
      }
    } catch (err) {
      alert('Transfer fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { key: 'upload', label: 'Upload' },
    { key: 'mapping', label: 'Mapping' },
    { key: 'abgleich', label: 'Abgleich' },
    { key: 'review', label: 'Ergebnis' }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  const getAktionLabel = (akt: string): string => {
    const labels: Record<string, string> = {
      '': 'OK',
      'DEF': 'Defekt',
      'DUB': 'Dublette',
      'BBB': 'Böse Buben',
      'BLL': 'Blacklist',
      'XBL': 'Erw. Blacklist',
      'XTE': 'Tel-Sperre',
      'ADN': 'Aktiv (RC)',
      'ADM': 'Aktiv (MC)',
      'INA': 'Inaktiv'
    };
    return labels[akt] || akt;
  };

  const getAktionColor = (akt: string): string => {
    if (!akt || akt === '') return '#10b981'; // green
    if (akt === 'DEF' || akt === 'DUB') return '#f59e0b'; // orange
    return '#ef4444'; // red
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <SubPageHeader title="Abgleich" />

      {/* View Mode Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ display: 'flex', gap: '0' }}>
            <button
              onClick={() => { setViewMode('new'); setSelectedHistoryId(null); setHistoryDetail(null); }}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                borderBottom: viewMode === 'new' ? '3px solid #003366' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: viewMode === 'new' ? '#003366' : '#6b7280',
                fontWeight: viewMode === 'new' ? '600' : '400',
                fontSize: '1rem',
                cursor: 'pointer'
              }}
            >
              Neuer Abgleich
            </button>
            <button
              onClick={() => { setViewMode('history'); loadHistory(); }}
              style={{
                padding: '1rem 1.5rem',
                border: 'none',
                borderBottom: viewMode === 'history' ? '3px solid #003366' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: viewMode === 'history' ? '#003366' : '#6b7280',
                fontWeight: viewMode === 'history' ? '600' : '400',
                fontSize: '1rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Historie
              {history.length > 0 && (
                <span style={{
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '10px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  {history.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'new' && (
      <>
      {/* Progress Steps */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          overflowX: 'auto'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: isMobile ? '500px' : 'auto'
          }}>
            {steps.map((s, idx) => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: step === s.key ? '#003366' : currentStepIndex > idx ? '#10b981' : '#e5e7eb',
                  color: step === s.key || currentStepIndex > idx ? 'white' : '#6b7280',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  flexShrink: 0
                }}>
                  {currentStepIndex > idx ? '✓' : idx + 1}
                </div>
                <div style={{
                  marginLeft: '0.75rem',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  color: step === s.key ? '#003366' : '#6b7280',
                  whiteSpace: 'nowrap'
                }}>
                  {s.label}
                </div>
                {idx < 3 && <div style={{ flex: 1, height: '2px', backgroundColor: '#e5e7eb', marginLeft: '1rem' }}></div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main style={{
        maxWidth: '1400px',
        margin: isMobile ? '1rem auto' : '2rem auto',
        padding: isMobile ? '0 1rem' : '0 2rem'
      }}>

        {/* STEP 1: Upload */}
        {step === 'upload' && (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb'
          }}>
            <h2 style={{ margin: '0 0 1rem 0', color: '#003366', fontSize: '1.5rem', fontWeight: '600' }}>
              Adressdaten für Abgleich hochladen
            </h2>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              CSV oder Excel-Datei mit Adressdaten auswählen. Die Daten werden gegen die Sperrlisten abgeglichen.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              disabled={loading}
              style={{
                display: 'block',
                width: '100%',
                padding: '1rem',
                border: '2px dashed #d1d5db',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: '#f9fafb'
              }}
            />
            {loading && <p style={{ marginTop: '1rem', color: '#003366' }}>Wird hochgeladen...</p>}
          </div>
        )}

        {/* STEP 2: Mapping */}
        {step === 'mapping' && (
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb'
          }}>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#003366', fontSize: '1.5rem', fontWeight: '600' }}>
              Spalten zuordnen
            </h2>
            <p style={{ margin: '0 0 1.5rem 0', color: '#6b7280' }}>
              Datei: <strong>{fileName}</strong> - Ordnen Sie die Spalten den Abgleich-Feldern zu
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {columns.map((col) => (
                <div key={col.name} style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#111827' }}>{col.name}</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {col.sample_values.slice(0, 2).join(' | ')}
                    </div>
                  </div>
                  <select
                    value={mapping[col.name] || ''}
                    onChange={(e) => handleMappingChange(col.name, e.target.value)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      minWidth: '200px'
                    }}
                  >
                    {abgleichFields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button
              onClick={handleSaveMapping}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: loading ? '#9ca3af' : '#003366',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              {loading ? 'Verarbeite...' : 'Abgleich starten'}
            </button>
          </div>
        )}

        {/* STEP 3: Abgleich läuft */}
        {step === 'abgleich' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.5rem 2rem',
              backgroundColor: '#003366',
              color: 'white'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Abgleich läuft...
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
                {fileName}
              </p>
            </div>

            {/* Progress Steps */}
            <div style={{ padding: '1.5rem 2rem' }}>
              {progress?.steps?.map((s, idx) => (
                <div
                  key={s.step}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 0',
                    borderBottom: idx < (progress?.steps?.length || 0) - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                >
                  {/* Status Icon */}
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem',
                    flexShrink: 0,
                    backgroundColor: s.status === 'done' ? '#10b981' : s.status === 'running' ? '#003366' : '#e5e7eb',
                    color: s.status === 'pending' ? '#9ca3af' : 'white'
                  }}>
                    {s.status === 'done' ? (
                      <span style={{ fontSize: '0.875rem' }}>✓</span>
                    ) : s.status === 'running' ? (
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    ) : (
                      <span style={{ fontSize: '0.75rem' }}>{idx + 1}</span>
                    )}
                  </div>

                  {/* Step Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: s.status === 'running' ? '600' : '400',
                      color: s.status === 'pending' ? '#9ca3af' : '#111827'
                    }}>
                      {s.description}
                    </div>
                    {s.status === 'done' && s.count > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.125rem' }}>
                        {s.count.toLocaleString()} Treffer
                      </div>
                    )}
                  </div>

                  {/* Duration */}
                  <div style={{
                    textAlign: 'right',
                    minWidth: '70px',
                    fontSize: '0.875rem',
                    color: s.status === 'done' ? '#059669' : '#9ca3af',
                    fontFamily: 'monospace'
                  }}>
                    {s.status === 'done' ? (
                      s.duration_ms >= 1000
                        ? `${(s.duration_ms / 1000).toFixed(1)}s`
                        : `${s.duration_ms}ms`
                    ) : s.status === 'running' ? (
                      '...'
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              ))}

              {/* Fallback wenn noch kein Progress */}
              {!progress?.steps && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  Starte Abgleich...
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: Review / Statistik */}
        {step === 'review' && !statistik && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            textAlign: 'center',
            border: '1px solid #e5e7eb'
          }}>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Lade Statistik...</p>
            <button
              onClick={async () => {
                if (!sourceId) return;
                try {
                  const response = await authFetch(`${API_BASE}/api/ingest/abgleich/${sourceId}/statistik`);
                  const data = await response.json();
                  console.log('Manual statistik load:', data);
                  if (data.success && data.data) {
                    const stats = data.data;
                    setStatistik({
                      brutto: stats.brutto1 || 0,
                      defekt: stats.defekt || 0,
                      brutto2: stats.brutto2 || 0,
                      dublette: stats.dublette || 0,
                      aktiv_rc: stats.aktiv_rc || 0,
                      aktiv_mc: stats.aktiv_mc || 0,
                      inaktiv_rc: stats.inaktiv_rc || 0,
                      inaktiv_mc: stats.inaktiv_mc || 0,
                      blacklist_rc: stats.blacklist_rc || 0,
                      blacklist_mc: stats.blacklist_mc || 0,
                      bbb: stats.bbb || 0,
                      xtel: stats.xtel || 0,
                      netto: stats.netto || 0,
                      quote_prozent: stats.quote ? parseFloat(stats.quote) * 100 : 0
                    });
                  }
                } catch (err) {
                  console.error('Failed to load statistik:', err);
                  alert('Fehler beim Laden der Statistik');
                }
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#003366',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Statistik neu laden
            </button>
          </div>
        )}
        {step === 'review' && statistik && (
          <div>
            {/* Statistik Header */}
            <div style={{
              backgroundColor: '#003366',
              color: 'white',
              padding: '1.5rem 2rem',
              borderRadius: '8px 8px 0 0',
              marginBottom: 0
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>
                Abgleich-Statistik: {fileName}
              </h2>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.8 }}>
                Quote: {statistik.quote_prozent}%
              </p>
            </div>

            {/* Statistik Tabelle */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              overflow: 'hidden',
              marginBottom: '1.5rem'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Prüfung</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Anzahl</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ backgroundColor: '#f0fdf4' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>Brutto (Eingabe)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '600' }}>{statistik.brutto.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>100%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Defekt</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.defekt.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.defekt / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '500' }}>= Brutto 2</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '500' }}>{statistik.brutto2.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}></td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#f59e0b' }}>- Dubletten</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#f59e0b' }}>{statistik.dublette.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.dublette / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Aktive Kunden (RC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.aktiv_rc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.aktiv_rc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Aktive Kunden (MC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.aktiv_mc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.aktiv_mc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Inaktive Kunden (RC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.inaktiv_rc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.inaktiv_rc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Inaktive Kunden (MC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.inaktiv_mc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.inaktiv_mc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Blacklist (RC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.blacklist_rc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.blacklist_rc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Blacklist (MC)</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.blacklist_mc.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.blacklist_mc / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Böse Buben</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.bbb.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.bbb / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem 1rem', color: '#dc2626' }}>- Telefon-Sperre</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#dc2626' }}>{statistik.xtel.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#6b7280' }}>{(statistik.xtel / statistik.brutto * 100).toFixed(2)}%</td>
                  </tr>
                  <tr style={{ backgroundColor: '#d1fae5' }}>
                    <td style={{ padding: '1rem', fontWeight: '700', fontSize: '1.1rem' }}>= NETTO</td>
                    <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', fontSize: '1.1rem', color: '#059669' }}>{statistik.netto.toLocaleString()}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#059669' }}>{statistik.quote_prozent}%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Filter Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              {(['all', 'netto', 'gesperrt'] as const).map(filter => (
                <button
                  key={filter}
                  onClick={() => loadRecords(filter)}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: recordFilter === filter ? '#003366' : 'white',
                    color: recordFilter === filter ? 'white' : '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {filter === 'all' ? 'Alle' : filter === 'netto' ? 'Netto (OK)' : 'Gesperrt'}
                </button>
              ))}
            </div>

            {/* Records Table */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb',
              overflowX: 'auto',
              marginBottom: '1.5rem'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <tr>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Status</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Name</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Adresse</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Herkunft</th>
                    <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Grund</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.75rem',
                          borderRadius: '9999px',
                          fontWeight: '500',
                          backgroundColor: r.chk === ' ' ? '#d1fae5' : '#fee2e2',
                          color: getAktionColor(r.akt)
                        }}>
                          {getAktionLabel(r.akt)}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>{r.vnm} {r.nnm}</div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem' }}>
                        <div style={{ color: '#111827' }}>{r.str}</div>
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{r.plz} {r.ort}</div>
                      </td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{r.hkn} / {r.lfr}</td>
                      <td style={{ padding: '0.75rem 1rem', color: '#6b7280', fontSize: '0.875rem' }}>{r.inf5}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleTransfer}
                disabled={loading || statistik.netto === 0}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (loading || statistik.netto === 0) ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (loading || statistik.netto === 0) ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {statistik.netto.toLocaleString()} Datensätze in WPool übertragen
              </button>
              <button
                onClick={() => {
                  setStep('upload');
                  setSourceId(null);
                  setColumns([]);
                  setMapping({});
                  setStatistik(null);
                  setRecords([]);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                Neuer Abgleich
              </button>
            </div>
          </div>
        )}
      </main>
      </>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '1rem' }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            {/* History List - hide on mobile when detail is selected */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              flex: selectedHistoryId && !isMobile ? '0 0 350px' : '1 1 100%',
              minWidth: isMobile ? '100%' : '280px',
              maxWidth: selectedHistoryId && !isMobile ? '350px' : '100%',
              display: selectedHistoryId && isMobile ? 'none' : 'block'
            }}>
              <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                  Vergangene Abgleiche
                </h3>
              </div>
              <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {history.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                    Keine Abgleiche vorhanden
                  </div>
                ) : (
                  history.map(item => (
                    <div
                      key={item.source_id}
                      onClick={() => loadHistoryDetail(item.source_id)}
                      style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f3f4f6',
                        cursor: 'pointer',
                        backgroundColor: selectedHistoryId === item.source_id ? '#f0f9ff' : 'transparent',
                        borderLeft: selectedHistoryId === item.source_id ? '3px solid #003366' : '3px solid transparent'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#111827', marginBottom: '0.25rem', fontSize: '0.9rem' }}>
                            {item.hkn || item.name}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.liste || item.file_name}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <div style={{
                            fontSize: '0.7rem',
                            padding: '0.125rem 0.4rem',
                            borderRadius: '4px',
                            backgroundColor: item.status === 'abgleich_done' ? '#d1fae5' : item.status === 'imported' ? '#dbeafe' : '#f3f4f6',
                            color: item.status === 'abgleich_done' ? '#065f46' : item.status === 'imported' ? '#1e40af' : '#374151'
                          }}>
                            {item.status === 'abgleich_done' ? 'Fertig' : item.status === 'imported' ? 'Importiert' : item.status}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.4rem', fontSize: '0.8rem', flexWrap: 'wrap' }}>
                        <span style={{ color: '#059669' }}>
                          Netto: {item.netto?.toLocaleString() || '-'}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          Brutto: {item.brutto1?.toLocaleString() || item.total_rows?.toLocaleString() || '-'}
                        </span>
                        {item.quote && (
                          <span style={{ color: '#6b7280' }}>
                            {(parseFloat(String(item.quote)) * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '0.4rem' }}>
                        {formatDateTime(item.created_at)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* History Detail */}
            {selectedHistoryId && historyDetail && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                flex: '1 1 400px',
                minWidth: 0
              }}>
                <div style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {/* Back button for mobile */}
                      {isMobile && (
                        <button
                          onClick={() => { setSelectedHistoryId(null); setHistoryDetail(null); }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem'
                          }}
                        >
                          ← Zurück
                        </button>
                      )}
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                        {historyDetail.statistik?.hkn || historyDetail.source?.name}
                      </h3>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                        {historyDetail.source?.file_name}
                      </span>
                      <span>{formatDate(historyDetail.source?.created_at)}</span>
                      <span style={{
                        padding: '0.125rem 0.4rem',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        backgroundColor: historyDetail.source?.status === 'abgleich_done' ? '#d1fae5' : '#fef3c7',
                        color: historyDetail.source?.status === 'abgleich_done' ? '#065f46' : '#92400e'
                      }}>
                        {historyDetail.source?.status === 'abgleich_done' ? 'Fertig' : 'Offen'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {historyDetail.source?.status !== 'abgleich_done' && (
                      <button
                        onClick={() => runAbgleichFromHistory(selectedHistoryId!)}
                        disabled={historyAbgleichRunning}
                        style={{
                          padding: '0.4rem 0.75rem',
                          backgroundColor: historyAbgleichRunning ? '#9ca3af' : '#003366',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: historyAbgleichRunning ? 'not-allowed' : 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}
                      >
                        {historyAbgleichRunning ? 'Läuft...' : 'Starten'}
                      </button>
                    )}
                    <button
                      onClick={() => deleteHistoryItem(selectedHistoryId)}
                      disabled={loading || historyAbgleichRunning}
                      style={{
                        padding: '0.4rem 0.75rem',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: (loading || historyAbgleichRunning) ? 'not-allowed' : 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      Löschen
                    </button>
                  </div>
                </div>

                {/* Progress when running from history */}
                {historyAbgleichRunning && (
                  <div style={{ padding: isMobile ? '1rem' : '1.5rem', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f0f9ff' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: isMobile ? '0.9rem' : '1rem', fontWeight: '600', color: '#003366', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid #bfdbfe',
                        borderTopColor: '#003366',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Abgleich läuft...
                    </h4>
                    {historyProgress?.steps?.map((s, idx) => (
                      <div
                        key={s.step}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.4rem 0',
                          fontSize: isMobile ? '0.8rem' : '0.85rem'
                        }}
                      >
                        <div style={{
                          width: isMobile ? '18px' : '20px',
                          height: isMobile ? '18px' : '20px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: isMobile ? '0.5rem' : '0.75rem',
                          flexShrink: 0,
                          backgroundColor: s.status === 'done' ? '#10b981' : s.status === 'running' ? '#003366' : '#e5e7eb',
                          color: s.status === 'pending' ? '#9ca3af' : 'white',
                          fontSize: '0.7rem'
                        }}>
                          {s.status === 'done' ? '✓' : s.status === 'running' ? '...' : idx + 1}
                        </div>
                        <div style={{ flex: 1, color: s.status === 'pending' ? '#9ca3af' : '#111827' }}>
                          {s.description}
                        </div>
                        {s.status === 'done' && !isMobile && (
                          <div style={{ fontSize: '0.8rem', color: '#059669', fontFamily: 'monospace' }}>
                            {s.duration_ms >= 1000 ? `${(s.duration_ms / 1000).toFixed(1)}s` : `${s.duration_ms}ms`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Statistics */}
                {historyDetail.statistik && (
                  <div style={{ padding: isMobile ? '1rem' : '1.5rem' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                      Statistik
                    </h4>
                    {isMobile ? (
                      /* Mobile: 2-column layout */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Brutto</span>
                          <span>{historyDetail.statistik.brutto1?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
                          <span style={{ fontWeight: '500' }}>Defekt</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.defekt?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Brutto2</span>
                          <span>{historyDetail.statistik.brutto2?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
                          <span style={{ fontWeight: '500' }}>Dubletten</span>
                          <span style={{ color: '#f59e0b' }}>{historyDetail.statistik.dublette?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Aktiv (RC)</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.aktiv_rc?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
                          <span style={{ fontWeight: '500' }}>Aktiv (MC)</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.aktiv_mc?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Blacklist (RC)</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.blacklist_rc?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
                          <span style={{ fontWeight: '500' }}>Blacklist (MC)</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.blacklist_mc?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Böse Buben</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.bbb?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem' }}>
                          <span style={{ fontWeight: '500' }}>XTEL</span>
                          <span style={{ color: '#dc2626' }}>{historyDetail.statistik.xtel?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#d1fae5', borderRadius: '6px', marginTop: '0.5rem' }}>
                          <span style={{ fontWeight: '700' }}>Netto</span>
                          <span style={{ fontWeight: '700', color: '#059669' }}>{historyDetail.statistik.netto?.toLocaleString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem', backgroundColor: '#d1fae5', borderRadius: '6px' }}>
                          <span style={{ fontWeight: '700' }}>Quote</span>
                          <span style={{ fontWeight: '700', color: '#059669' }}>{(parseFloat(historyDetail.statistik.quote) * 100).toFixed(1)}%</span>
                        </div>
                      </div>
                    ) : (
                      /* Desktop: 4-column table */
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <tbody>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Brutto</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{historyDetail.statistik.brutto1?.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Defekt</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.defekt?.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Brutto2</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{historyDetail.statistik.brutto2?.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Dubletten</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#f59e0b' }}>{historyDetail.statistik.dublette?.toLocaleString()}</td>
                          </tr>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Aktiv (RC)</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.aktiv_rc?.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Aktiv (MC)</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.aktiv_mc?.toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Blacklist (RC)</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.blacklist_rc?.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Blacklist (MC)</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.blacklist_mc?.toLocaleString()}</td>
                          </tr>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>Böse Buben</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.bbb?.toLocaleString()}</td>
                            <td style={{ padding: '0.5rem 1rem', fontWeight: '500' }}>XTEL</td>
                            <td style={{ padding: '0.5rem 1rem', textAlign: 'right', color: '#dc2626' }}>{historyDetail.statistik.xtel?.toLocaleString()}</td>
                          </tr>
                          <tr style={{ backgroundColor: '#d1fae5' }}>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: '700', fontSize: '1rem' }}>Netto</td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', fontSize: '1rem', color: '#059669' }}>
                              {historyDetail.statistik.netto?.toLocaleString()}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: '700', fontSize: '1rem' }}>Quote</td>
                            <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', fontSize: '1rem', color: '#059669' }}>
                              {(parseFloat(historyDetail.statistik.quote) * 100).toFixed(1)}%
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    {/* Transfer Button / Status */}
                    {historyDetail.statistik.netto > 0 && historyDetail.source?.status === 'abgleich_done' && (
                      <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                        {historyDetail.statistik.transferred_at ? (
                          <div style={{
                            padding: isMobile ? '0.75rem 1rem' : '0.75rem 2rem',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #bbf7d0',
                            borderRadius: '8px',
                            color: '#166534',
                            fontSize: isMobile ? '0.85rem' : '0.95rem'
                          }}>
                            <span style={{ fontWeight: '600' }}>
                              {historyDetail.statistik.netto?.toLocaleString()} Datensätze
                            </span>
                            {isMobile ? <br /> : ' '}wurden am{' '}
                            <span style={{ fontWeight: '600' }}>
                              {formatDateTime(historyDetail.statistik.transferred_at)}
                            </span>
                            {isMobile ? <br /> : ' '}in WPool übertragen
                          </div>
                        ) : (
                          <button
                            onClick={() => transferFromHistory(selectedHistoryId!, historyDetail.statistik.netto)}
                            disabled={historyTransferLoading}
                            style={{
                              padding: isMobile ? '0.75rem 1rem' : '0.75rem 2rem',
                              backgroundColor: historyTransferLoading ? '#9ca3af' : '#059669',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: historyTransferLoading ? 'not-allowed' : 'pointer',
                              fontSize: isMobile ? '0.85rem' : '1rem',
                              fontWeight: '600',
                              width: isMobile ? '100%' : 'auto'
                            }}
                          >
                            {historyTransferLoading
                              ? 'Übertrage...'
                              : isMobile
                                ? `${historyDetail.statistik.netto?.toLocaleString()} → WPool`
                                : `${historyDetail.statistik.netto?.toLocaleString()} Datensätze in WPool übertragen`
                            }
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Summary by Action */}
                {historyDetail.summary && historyDetail.summary.length > 0 && (
                  <div style={{ padding: isMobile ? '0 1rem 1rem' : '0 1.5rem 1.5rem' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#374151' }}>
                      Aufteilung nach Aktion
                      {!isMobile && (
                        <span style={{ fontSize: '0.8rem', fontWeight: '400', color: '#6b7280', marginLeft: '0.5rem' }}>
                          (Klicken zum Anzeigen)
                        </span>
                      )}
                    </h4>
                    {isMobile ? (
                      /* Mobile: Card layout */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {historyDetail.summary.map((s: any, idx: number) => (
                          <div
                            key={idx}
                            onClick={() => loadRecordsByAction(selectedHistoryId!, s.aktion)}
                            style={{
                              padding: '0.75rem',
                              backgroundColor: selectedAction === s.aktion ? '#eff6ff' : '#f9fafb',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              borderLeft: selectedAction === s.aktion ? '3px solid #003366' : '3px solid transparent'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                              <span style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: s.aktion === '' ? '#d1fae5' : '#fee2e2',
                                color: s.aktion === '' ? '#065f46' : '#dc2626'
                              }}>
                                {s.aktion || 'OK'}
                              </span>
                              <span style={{ fontWeight: '600' }}>{s.anzahl?.toLocaleString()}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>{s.beschreibung}</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadRecordsByAction(selectedHistoryId!, s.aktion);
                                }}
                                style={{
                                  padding: '0.2rem 0.4rem',
                                  backgroundColor: '#fff',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.7rem',
                                  color: '#374151'
                                }}
                              >
                                CSV
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Desktop: Table */
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f3f4f6' }}>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'left', fontWeight: '600' }}>Aktion</th>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'left', fontWeight: '600' }}>Beschreibung</th>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: '600' }}>Anzahl</th>
                            <th style={{ padding: '0.5rem 1rem', textAlign: 'center', fontWeight: '600', width: '100px' }}>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyDetail.summary.map((s: any, idx: number) => (
                            <tr
                              key={idx}
                              onClick={() => loadRecordsByAction(selectedHistoryId!, s.aktion)}
                              style={{
                                backgroundColor: selectedAction === s.aktion ? '#eff6ff' : (idx % 2 === 0 ? 'white' : '#f9fafb'),
                                cursor: 'pointer',
                                borderLeft: selectedAction === s.aktion ? '3px solid #003366' : '3px solid transparent'
                              }}
                            >
                              <td style={{ padding: '0.5rem 1rem' }}>
                                <span style={{
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  backgroundColor: s.aktion === '' ? '#d1fae5' : '#fee2e2',
                                  color: s.aktion === '' ? '#065f46' : '#dc2626'
                                }}>
                                  {s.aktion || 'OK'}
                                </span>
                              </td>
                              <td style={{ padding: '0.5rem 1rem' }}>{s.beschreibung}</td>
                              <td style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: '500' }}>
                                {s.anzahl?.toLocaleString()}
                              </td>
                              <td style={{ padding: '0.5rem 1rem', textAlign: 'center' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadRecordsByAction(selectedHistoryId!, s.aktion);
                                  }}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: '#f3f4f6',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    color: '#374151'
                                  }}
                                  title="Als CSV herunterladen"
                                >
                                  CSV
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* Records - filtered by action or sample records */}
                <div style={{ padding: isMobile ? '0 1rem 1rem' : '0 1.5rem 1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: isMobile ? '0.5rem' : '0',
                    marginBottom: '1rem'
                  }}>
                    <h4 style={{ margin: 0, fontSize: isMobile ? '0.9rem' : '1rem', fontWeight: '600', color: '#374151' }}>
                      {selectedAction !== null ? (
                        <>
                          Datensätze: {selectedAction === '' ? 'Netto (OK)' : selectedAction}
                          <span style={{ fontSize: '0.8rem', fontWeight: '400', color: '#6b7280', marginLeft: '0.5rem' }}>
                            ({actionRecords.length})
                          </span>
                        </>
                      ) : (
                        <>Beispiel-Datensätze</>
                      )}
                    </h4>
                    {selectedAction !== null && (
                      <button
                        onClick={() => { setSelectedAction(null); setActionRecords([]); }}
                        style={{
                          padding: '0.25rem 0.75rem',
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color: '#374151'
                        }}
                      >
                        Zurück
                      </button>
                    )}
                  </div>

                  {actionRecordsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                      Lade Datensätze...
                    </div>
                  ) : isMobile ? (
                    /* Mobile: Card layout */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                      {(selectedAction !== null ? actionRecords : (historyDetail.sample_records || [])).map((r: any, idx: number) => (
                        <div key={r.id} style={{
                          padding: '0.75rem',
                          backgroundColor: idx % 2 === 0 ? '#f9fafb' : 'white',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                            <span style={{
                              padding: '0.125rem 0.375rem',
                              borderRadius: '3px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              backgroundColor: r.chk === ' ' ? '#d1fae5' : '#fee2e2',
                              color: r.chk === ' ' ? '#065f46' : '#dc2626'
                            }}>
                              {r.akt || 'OK'}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{r.hkn}</span>
                          </div>
                          <div style={{ fontWeight: '500', fontSize: '0.85rem' }}>{r.vnm} {r.nnm}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.str}</div>
                          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.plz} {r.ort}</div>
                          {r.inf5 && <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>{r.inf5}</div>}
                        </div>
                      ))}
                      {(selectedAction !== null ? actionRecords : (historyDetail.sample_records || [])).length === 0 && (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                          Keine Datensätze vorhanden
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Desktop: Table */
                    <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f3f4f6' }}>
                          <tr>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Status</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Name</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Straße</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>PLZ/Ort</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Herkunft</th>
                            <th style={{ padding: '0.5rem', textAlign: 'left', whiteSpace: 'nowrap' }}>Info</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedAction !== null ? actionRecords : (historyDetail.sample_records || [])).map((r: any, idx: number) => (
                            <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                              <td style={{ padding: '0.5rem' }}>
                                <span style={{
                                  padding: '0.125rem 0.375rem',
                                  borderRadius: '3px',
                                  fontSize: '0.7rem',
                                  fontWeight: '600',
                                  backgroundColor: r.chk === ' ' ? '#d1fae5' : '#fee2e2',
                                  color: r.chk === ' ' ? '#065f46' : '#dc2626'
                                }}>
                                  {r.akt || 'OK'}
                                </span>
                              </td>
                              <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{r.vnm} {r.nnm}</td>
                              <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{r.str}</td>
                              <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{r.plz} {r.ort}</td>
                              <td style={{ padding: '0.5rem', whiteSpace: 'nowrap', color: '#6b7280' }}>{r.hkn}</td>
                              <td style={{ padding: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>{r.inf5}</td>
                            </tr>
                          ))}
                          {(selectedAction !== null ? actionRecords : (historyDetail.sample_records || [])).length === 0 && (
                            <tr>
                              <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                                Keine Datensätze vorhanden
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* No Selection */}
            {!selectedHistoryId && !isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                padding: '3rem',
                color: '#6b7280',
                flex: '1 1 400px'
              }}>
                Wähle einen Abgleich aus der Liste, um Details zu sehen.
              </div>
            )}
          </div>
        </main>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
// Build 20251229-responsive
